import { create } from "zustand";

export interface User {
  id: string;
  email: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  banner_url: string | null;
  bio: string | null;
  status: "online" | "idle" | "dnd" | "offline";
  custom_status: string | null;
  created_at: string;
}

export interface Server {
  id: string;
  name: string;
  description: string | null;
  icon_url: string | null;
  banner_url: string | null;
  owner_id: string;
  invite_code: string;
  is_public: boolean;
  member_count: number;
  created_at: string;
}

export interface Channel {
  id: string;
  server_id: string;
  category_id: string | null;
  name: string;
  topic: string | null;
  type: "text" | "voice" | "announcement";
  position: number;
  created_at: string;
}

export interface Category {
  id: string;
  server_id: string;
  name: string;
  position: number;
}

export interface Message {
  id: string;
  channel_id: string;
  author_id: string;
  content: string;
  reply_to_id: string | null;
  edited: boolean;
  created_at: string;
  updated_at: string;
  author?: User;
  attachments?: Attachment[];
  reactions?: Reaction[];
}

export interface Attachment {
  id: string;
  message_id: string;
  name: string;
  url: string;
  type: string;
  size: number;
}

export interface Reaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
}

export interface DirectMessage {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  read: boolean;
  created_at: string;
  sender?: User;
  receiver?: User;
}

export interface ServerMember {
  id: string;
  server_id: string;
  user_id: string;
  nickname: string | null;
  joined_at: string;
  user?: User;
}

interface AppState {
  // Current user
  user: User | null;
  setUser: (user: User | null) => void;

  // Servers
  servers: Server[];
  setServers: (servers: Server[]) => void;
  addServer: (server: Server) => void;
  updateServer: (id: string, data: Partial<Server>) => void;
  removeServer: (id: string) => void;

  // Active selections
  activeServerId: string | null;
  setActiveServerId: (id: string | null) => void;
  activeChannelId: string | null;
  setActiveChannelId: (id: string | null) => void;
  activeDMUserId: string | null;
  setActiveDMUserId: (id: string | null) => void;

  // Channels
  channels: Channel[];
  setChannels: (channels: Channel[]) => void;
  addChannel: (channel: Channel) => void;

  // Categories
  categories: Category[];
  setCategories: (categories: Category[]) => void;

  // Messages
  messages: Message[];
  setMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  updateMessage: (id: string, data: Partial<Message>) => void;
  removeMessage: (id: string) => void;

  // Members
  members: ServerMember[];
  setMembers: (members: ServerMember[]) => void;

  // DMs
  dmUsers: User[];
  setDMUsers: (users: User[]) => void;
  directMessages: DirectMessage[];
  setDirectMessages: (messages: DirectMessage[]) => void;
  addDirectMessage: (message: DirectMessage) => void;

  // UI State
  view: "servers" | "dms" | "discover" | "settings";
  setView: (view: "servers" | "dms" | "discover" | "settings") => void;
  showMemberList: boolean;
  setShowMemberList: (show: boolean) => void;
  
  // Typing indicators
  typingUsers: { channelId: string; user: User }[];
  addTypingUser: (channelId: string, user: User) => void;
  removeTypingUser: (channelId: string, userId: string) => void;

  // Voice state
  voiceChannelId: string | null;
  setVoiceChannelId: (id: string | null) => void;
  isMuted: boolean;
  setIsMuted: (muted: boolean) => void;
  isDeafened: boolean;
  setIsDeafened: (deafened: boolean) => void;
}

// Initial empty state - data loaded from API
const initialServers: Server[] = [];
const initialChannels: Channel[] = [];
const initialCategories: Category[] = [];
const initialMessages: Message[] = [];
const initialMembers: ServerMember[] = [];

export const useAppStore = create<AppState>((set) => ({
  // User - loaded from API
  user: null,
  setUser: (user) => set({ user }),

  // Servers - loaded from API
  servers: initialServers,
  setServers: (servers) => set({ servers }),
  addServer: (server) => set((state) => ({ servers: [...state.servers, server] })),
  updateServer: (id, data) =>
    set((state) => ({
      servers: state.servers.map((s) => (s.id === id ? { ...s, ...data } : s)),
    })),
  removeServer: (id) =>
    set((state) => ({ servers: state.servers.filter((s) => s.id !== id) })),

  // Active selections - loaded from API
  activeServerId: null,
  setActiveServerId: (id) => set({ activeServerId: id }),
  activeChannelId: null,
  setActiveChannelId: (id) => set({ activeChannelId: id }),
  activeDMUserId: null,
  setActiveDMUserId: (id) => set({ activeDMUserId: id }),

  // Channels - loaded from API
  channels: initialChannels,
  setChannels: (channels) => set({ channels }),
  addChannel: (channel) => set((state) => ({ channels: [...state.channels, channel] })),

  // Categories - loaded from API
  categories: initialCategories,
  setCategories: (categories) => set({ categories }),

  // Messages - loaded from API
  messages: initialMessages,
  setMessages: (messages) => set({ messages }),
  addMessage: (message) => set((state) => ({ messages: [...state.messages, message] })),
  updateMessage: (id, data) =>
    set((state) => ({
      messages: state.messages.map((m) => (m.id === id ? { ...m, ...data } : m)),
    })),
  removeMessage: (id) =>
    set((state) => ({ messages: state.messages.filter((m) => m.id !== id) })),

  // Members - loaded from API
  members: initialMembers,
  setMembers: (members) => set({ members }),

  // DMs
  dmUsers: [],
  setDMUsers: (users) => set({ dmUsers: users }),
  directMessages: [],
  setDirectMessages: (messages) => set({ directMessages: messages }),
  addDirectMessage: (message) =>
    set((state) => ({ directMessages: [...state.directMessages, message] })),

  // UI State
  view: "servers",
  setView: (view) => set({ view }),
  showMemberList: true,
  setShowMemberList: (show) => set({ showMemberList: show }),

  // Typing
  typingUsers: [],
  addTypingUser: (channelId, user) =>
    set((state) => {
      const exists = state.typingUsers.some(
        (t) => t.channelId === channelId && t.user.id === user.id
      );
      if (exists) return state;
      return { typingUsers: [...state.typingUsers, { channelId, user }] };
    }),
  removeTypingUser: (channelId, userId) =>
    set((state) => ({
      typingUsers: state.typingUsers.filter(
        (t) => !(t.channelId === channelId && t.user.id === userId)
      ),
    })),

  // Voice
  voiceChannelId: null,
  setVoiceChannelId: (id) => set({ voiceChannelId: id }),
  isMuted: false,
  setIsMuted: (muted) => set({ isMuted: muted }),
  isDeafened: false,
  setIsDeafened: (deafened) => set({ isDeafened: deafened }),
}));
